// RapidOrderEntry.js
// Right-pane order entry: autocomplete search + pending queue + rapid order list chips.
// React 16.14 compatible — class components.
//
// Props:
//   api            the api object created in RapidCcsApp           (required)
//                  (only api.createOrderSearch is used here)
//   queue          [{ID, PrimaryOrder}]                            (required)
//   onAddOrder     (orderObj) => void — {ID, MatchedTerm, PrimaryOrder}
//   onRemoveOrder  (id) => void
//   rapidLists     [{Name, Orders:[{Id,Name}]}] (or legacy OrderIds)
//   onFireList     (list) => void — add all orders in a list to the queue

import React from "react";
import { Modal } from "./RapidCcsModals";

let _lastPointerType = null; // "mouse" | "pen" | "touch" | null (unknown)
if (typeof window !== "undefined") {
  if (window.PointerEvent) {
    window.addEventListener(
      "pointerdown",
      function (e) {
        _lastPointerType = e.pointerType || null;
      },
      { capture: true, passive: true },
    );
  } else {
    // Very old browsers: fall back to touch/mouse events.
    window.addEventListener(
      "touchstart",
      function () {
        _lastPointerType = "touch";
      },
      { capture: true, passive: true },
    );
    window.addEventListener(
      "mousedown",
      function () {
        _lastPointerType = "mouse";
      },
      { capture: true, passive: true },
    );
  }
}

function canHover() {
  if (typeof window === "undefined") return true; // SSR-safe default

  if (_lastPointerType === "touch") return false;
  if (_lastPointerType === "mouse" || _lastPointerType === "pen") return true;

  const mm = typeof window.matchMedia === "function" ? window.matchMedia : null;
  if (!mm) return true;
  return mm("(hover: hover) and (pointer: fine)").matches;
}

// Normalize a rapid list's orders into [{Id, Name}], tolerating either the new
// `Orders: [{Id, Name}]` shape or a legacy `OrderIds: [n]` shape.
function listOrders(list) {
  if (Array.isArray(list.Orders)) {
    return list.Orders.map(function (o) {
      return { Id: o.Id, Name: o.Name || "Order #" + o.Id };
    });
  }
  if (Array.isArray(list.OrderIds)) {
    return list.OrderIds.map(function (id) {
      return { Id: id, Name: "Order #" + id };
    });
  }
  return [];
}

class RapidListChip extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hover: false, confirming: false };
  }

  handleEnter = () => {
    this.setState({ hover: true });
  };
  handleLeave = () => {
    this.setState({ hover: false });
  };

  // Actually fires the list — called directly on desktop (hover already
  // showed the contents), or from the confirm modal on touch devices.
  fire = () => {
    this.props.onFire(this.props.list);
    this.setState({ hover: false, confirming: false });
  };

  handleChipClick = () => {
    if (canHover()) {
      // Desktop/mouse: hovering already previewed the contents, so a click
      // fires the list right away, same as before.
      this.fire();
    } else {
      // Touch: there's no hover preview, so require an explicit confirm
      // step that shows the list's contents before it's fired.
      this.setState({ confirming: true });
    }
  };

  closeConfirm = () => {
    this.setState({ confirming: false });
  };

  render() {
    const list = this.props.list;
    const orders = listOrders(list);
    const hover = this.state.hover;
    const confirming = this.state.confirming;

    return (
      <span
        className="rccs-rol__wrap"
        onMouseEnter={this.handleEnter}
        onMouseLeave={this.handleLeave}
      >
        <button
          type="button"
          className="rccs-rol__chip"
          onClick={this.handleChipClick}
          title={"Order all " + orders.length + " items"}
        >
          + {list.Name}
        </button>
        {hover && orders.length > 0 && (
          <div className="rccs-rol__pop" role="tooltip">
            <div className="rccs-rol__pop-title">
              {list.Name} — {orders.length} orders
            </div>
            <ul>
              {orders.map(function (o, i) {
                return <li key={o.Id + "-" + i}>{o.Name}</li>;
              })}
            </ul>
          </div>
        )}
        {confirming && (
          <Modal
            title={list.Name}
            onClose={this.closeConfirm}
            footer={
              <React.Fragment>
                <button className="rccs-btn" onClick={this.closeConfirm}>
                  Cancel
                </button>
                <button
                  className="rccs-btn rccs-btn--primary"
                  onClick={this.fire}
                >
                  Order {orders.length} {orders.length === 1 ? "item" : "items"}
                </button>
              </React.Fragment>
            }
          >
            {orders.length === 0 ? (
              <p style={{ color: "var(--ink-faint)" }}>
                This list has no orders.
              </p>
            ) : (
              <ul className="rccs-rol__confirm-list">
                {orders.map(function (o, i) {
                  return <li key={o.Id + "-" + i}>{o.Name}</li>;
                })}
              </ul>
            )}
          </Modal>
        )}
      </span>
    );
  }
}

class MobileOrderSearch extends React.Component {
  render() {
    const {
      term,
      results,
      searching,
      activeIdx,
      onTerm,
      onKeyDown,
      onAdd,
      inQueue,
      onClose,
      queue,
      onRemove,
      inputRef,
    } = this.props;

    const showHint = term.trim().length < 3;

    return (
      <div className="rccs-mobile-search" role="dialog" aria-modal="true">
        <div className="rccs-mobile-search__header">
          <span className="rccs-mobile-search__title">Search orders</span>
          <button
            type="button"
            className="rccs-mobile-search__close"
            onClick={onClose}
            aria-label="Close search"
          >
            ×
          </button>
        </div>

        <div className="rccs-mobile-search__box">
          <span className="rccs-order-search__icon" aria-hidden="true">
            ⌕
          </span>
          <input
            ref={inputRef}
            type="text"
            value={term}
            onChange={(e) => {
              onTerm(e.target.value);
            }}
            onKeyDown={onKeyDown}
            placeholder="Type 3+ characters…"
            aria-label="Search orders"
            autoComplete="off"
            autoFocus
          />
        </div>

        {queue.length > 0 && (
          <div className="rccs-mobile-search__queue">
            {queue.map((o) => (
              <span className="rccs-mobile-search__queue-chip" key={o.ID}>
                <span className="rccs-mobile-search__queue-chip-name">
                  {o.PrimaryOrder}
                </span>
                <button
                  type="button"
                  className="rccs-mobile-search__queue-chip-x"
                  onClick={() => onRemove(o.ID)}
                  aria-label={"Remove " + o.PrimaryOrder}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="rccs-mobile-search__results">
          {showHint ? (
            <div className="rccs-ac__hint">
              Keep typing — 3+ characters to search.
            </div>
          ) : searching ? (
            <div className="rccs-ac__hint">Searching…</div>
          ) : results.length === 0 ? (
            <div className="rccs-ac__hint">No matching orders.</div>
          ) : (
            results.map((o, idx) => {
              const already = inQueue(o.ID);
              return (
                <div
                  key={o.ID}
                  className={
                    "rccs-mobile-search__item" +
                    (idx === activeIdx
                      ? " rccs-mobile-search__item--active"
                      : "") +
                    (already ? " rccs-mobile-search__item--queued" : "")
                  }
                  onClick={() => {
                    if (!already) onAdd(o);
                  }}
                >
                  <span>{o.MatchedTerm}</span>
                  {already && (
                    <span className="rccs-mobile-search__queued-tag">
                      queued
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="rccs-mobile-search__footer">
          <button
            type="button"
            className="rccs-btn rccs-btn--primary"
            onClick={onClose}
          >
            Done{queue.length > 0 ? " (" + queue.length + ")" : ""}
          </button>
        </div>
      </div>
    );
  }
}

class RapidOrderEntry extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      term: "",
      results: [],
      open: false,
      activeIdx: -1,
      searching: false,
      mobileSearchOpen: false,
    };
    this.boxRef = React.createRef();
    this.inlineInputRef = React.createRef();
    this.mobileInputRef = React.createRef();
    this.search = null; // created in componentDidMount from props.api
    this.searchSeq = 0; // guards against a slow, older search overwriting a newer one
  }

  componentDidMount() {
    // All requests stem from RapidCcsApp — the search helper comes off the
    // api prop, never imported directly.
    this.search = this.props.api.createOrderSearch({
      minLength: 3,
      maxResults: 15,
    });
    document.addEventListener("mousedown", this.handleDocMouseDown);
  }

  componentWillUnmount() {
    if (this.search) this.search.cancel();
    document.removeEventListener("mousedown", this.handleDocMouseDown);
    if (this.state.mobileSearchOpen) this.restoreBodyScroll();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.mobileSearchOpen !== this.state.mobileSearchOpen) {
      if (this.state.mobileSearchOpen) {
        this.lockBodyScroll();
      } else {
        this.restoreBodyScroll();
      }
    }
  }

  lockBodyScroll = () => {
    if (typeof document === "undefined") return;
    this._prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  };

  restoreBodyScroll = () => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = this._prevBodyOverflow || "";
  };

  // Close autocomplete on outside click.
  handleDocMouseDown = (e) => {
    if (this.boxRef.current && !this.boxRef.current.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  onTerm = (v) => {
    this.setState({ term: v, open: true, activeIdx: -1 });

    const trimmed = v.trim();
    if (trimmed.length < 3) {
      // Below minLength — nothing to search, and nothing "pending".
      this.searchSeq += 1;
      this.setState({ results: [], searching: false });
      return;
    }

    // Bump the sequence and clear stale results so the empty-state message
    // can't flash before this search actually resolves.
    const seq = ++this.searchSeq;
    this.setState({ searching: true, results: [] });

    this.search(
      v,
      (list) => {
        // A newer keystroke's search may have resolved first — ignore
        // this response if it's no longer the latest one in flight.
        if (seq !== this.searchSeq) return;
        this.setState({ results: list, searching: false });
      },
      () => {
        if (seq !== this.searchSeq) return;
        this.setState({ results: [], searching: false });
      },
    );
  };

  add = (o) => {
    this.props.onAddOrder(o);
    this.setState({
      term: "",
      results: [],
      open: false,
      activeIdx: -1,
      searching: false,
    });
  };

  openMobileSearch = () => {
    if (this.inlineInputRef.current) this.inlineInputRef.current.blur();
    this.setState({ mobileSearchOpen: true, open: false });
  };

  closeMobileSearch = () => {
    this.setState({
      mobileSearchOpen: false,
      term: "",
      results: [],
      open: false,
      activeIdx: -1,
      searching: false,
    });
    this.searchSeq += 1; // invalidate any in-flight search
  };

  onKeyDown = (e) => {
    const { open, results, activeIdx, mobileSearchOpen } = this.state;

    if (e.key === "Escape") {
      // Escape needs to work even before any results have loaded, so this
      // is handled before the open/results guard below.
      if (mobileSearchOpen) {
        this.closeMobileSearch();
      } else {
        this.setState({ open: false });
      }
      return;
    }

    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setState(function (prevState) {
        return {
          activeIdx: Math.min(
            prevState.activeIdx + 1,
            prevState.results.length - 1,
          ),
        };
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setState(function (prevState) {
        return { activeIdx: Math.max(prevState.activeIdx - 1, 0) };
      });
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && activeIdx < results.length) {
        e.preventDefault();
        this.add(results[activeIdx]);
      }
    }
  };

  handleFocus = () => {
    if (!canHover()) {
      this.openMobileSearch();
      return;
    }
    if (this.state.results.length) this.setState({ open: true });
  };

  inQueue = (id) => {
    const queue = this.props.queue || [];
    return queue.some(function (x) {
      return x.ID === id;
    });
  };

  render() {
    const { rapidLists, onFireList, onRemoveOrder } = this.props;
    const queue = this.props.queue || [];
    const { term, results, open, activeIdx, searching, mobileSearchOpen } =
      this.state;

    return (
      <div>
        <div className="rccs-bullet-row">
          {/* Rapid order list quick-fire (hover a chip to see its orders) */}
          {rapidLists && rapidLists.length > 0 && (
            <div className="rccs-rol">
              {rapidLists.map(function (l) {
                return (
                  <RapidListChip key={l.Name} list={l} onFire={onFireList} />
                );
              })}
            </div>
          )}
          <button className="rccs-rol__chip" onClick={this.props.openLists}>
            Create Custom Order List
          </button>
        </div>

        {/* Search box */}
        <div className="rccs-order-search" ref={this.boxRef}>
          <span className="rccs-order-search__icon" aria-hidden="true">
            ⌕
          </span>
          <input
            ref={this.inlineInputRef}
            type="text"
            value={term}
            onChange={(e) => {
              this.onTerm(e.target.value);
            }}
            onKeyDown={this.onKeyDown}
            onFocus={this.handleFocus}
            placeholder="Search orders — type 3+ characters…"
            aria-label="Search orders"
            autoComplete="off"
          />
          {open && term.trim().length >= 3 && (
            <div className="rccs-ac">
              {searching ? (
                <div className="rccs-ac__hint">Searching…</div>
              ) : results.length === 0 ? (
                <div className="rccs-ac__hint">No matching orders.</div>
              ) : (
                results.map((o, idx) => {
                  const already = this.inQueue(o.ID);
                  return (
                    <div
                      key={o.ID}
                      className={
                        "rccs-ac__item" +
                        (idx === activeIdx ? " rccs-ac__item--active" : "")
                      }
                      onMouseEnter={() => {
                        this.setState({ activeIdx: idx });
                      }}
                      onClick={() => {
                        if (!already) this.add(o);
                      }}
                      style={
                        already ? { opacity: 0.5, cursor: "default" } : null
                      }
                    >
                      {/* Show only the matched term in suggestions. */}
                      <div className="rccs-ac__matched-main">
                        {o.MatchedTerm}
                        {already && (
                          <span
                            style={{
                              fontSize: 11,
                              color: "var(--teal)",
                              marginLeft: 6,
                            }}
                          >
                            queued
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Pending queue — shows PrimaryOrder (the display name once selected) */}
        <div style={{ marginTop: 12 }}>
          {queue.length === 0 ? (
            <div className="rccs-queue--empty">
              No pending orders. Search above or fire a rapid list.
            </div>
          ) : (
            <div className="rccs-queue">
              {queue.map(function (o) {
                return (
                  <div className="rccs-queue__item" key={o.ID}>
                    <span className="rccs-queue__name">{o.PrimaryOrder}</span>
                    <button
                      className="rccs-queue__x"
                      onClick={function () {
                        onRemoveOrder(o.ID);
                      }}
                      aria-label={"Remove " + o.PrimaryOrder}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {mobileSearchOpen && (
          <MobileOrderSearch
            term={term}
            results={results}
            searching={searching}
            activeIdx={activeIdx}
            onTerm={this.onTerm}
            onKeyDown={this.onKeyDown}
            onAdd={this.add}
            inQueue={this.inQueue}
            onClose={this.closeMobileSearch}
            queue={queue}
            onRemove={onRemoveOrder}
            inputRef={this.mobileInputRef}
          />
        )}
      </div>
    );
  }
}

export default RapidOrderEntry;
